import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { Badge, Btn as Button, Modal } from '../../components/ui';
import toast from 'react-hot-toast';

const DOCUMENT_TYPES = [
    { id: 'id_proof', label: 'ID Proof (Aadhaar/PAN/Voter ID)' },
    { id: 'police_verification', label: 'Police Verification' },
    { id: 'address_proof', label: 'Address Proof' },
    { id: 'professional_license', label: 'Professional License' }
];

const getErrorMessage = (error, defaultMsg = "Failed to upload document") => {
    if (!error) return defaultMsg;
    if (typeof error === 'string' && error.trim()) return error.trim();

    // 1. Check for validation errors object/array (from API response or axios interceptor)
    const errorsObj = error?.errors || error?.response?.data?.errors || error?.data?.errors;
    if (errorsObj) {
        if (typeof errorsObj === 'string' && errorsObj.trim()) return errorsObj.trim();
        if (Array.isArray(errorsObj) && errorsObj.length > 0) {
            const first = errorsObj[0];
            if (typeof first === 'string' && first.trim()) return first.trim();
            if (first?.message && typeof first.message === 'string') return first.message.trim();
        }
        if (typeof errorsObj === 'object') {
            const allErrors = Object.values(errorsObj).flat().filter(Boolean);
            if (allErrors.length > 0) {
                const first = allErrors[0];
                if (typeof first === 'string' && first.trim()) return first.trim();
                if (first?.message && typeof first.message === 'string') return first.message.trim();
            }
        }
    }

    // 2. Check candidate message strings
    const candidateMsg = 
        error?.message ||
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.error ||
        error?.error;

    const genericMessages = [
        "validation error",
        "validation failed",
        "validation failed.",
        "error",
        "an unexpected error occurred",
        "failed to upload document",
        "failed to upload document."
    ];

    if (candidateMsg && typeof candidateMsg === 'string') {
        const trimmed = candidateMsg.trim();
        if (trimmed && !genericMessages.includes(trimmed.toLowerCase())) {
            return trimmed;
        }
    }

    // 3. Status code specific messages
    const status = error?.status || error?.response?.status;
    if (status === 413) return "File size is too large for the server. Please upload a file under 5MB.";
    if (status === 415) return "Unsupported file type. Please upload a PDF, JPG, JPEG, PNG, or WEBP file.";
    if (status === 422) return "Invalid file or document type. Please verify the file requirements.";
    if (status === 403) return "You do not have permission to manage documents for this service provider.";
    if (status === 404) return "Service provider not found or upload endpoint unavailable.";

    if (candidateMsg && typeof candidateMsg === 'string' && candidateMsg.trim()) {
        return candidateMsg.trim();
    }

    return defaultMsg;
};

export default function ServiceProviderDocuments({ providerId }) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingType, setUploadingType] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`/api/v1/visitor-service-providers/${providerId}/documents`);
            setDocuments(data.data || []);
        } catch (error) {
            console.error("Error fetching documents", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (providerId) {
            fetchDocuments();
        }
    }, [providerId]);

    const handleUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
        const ext = file.name?.split('.').pop()?.toLowerCase();
        if (!ext || !allowedExtensions.includes(ext)) {
            toast.error("Please upload a valid document or image (PDF, JPG, JPEG, PNG, WEBP)");
            e.target.value = '';
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error("File size must be less than 5MB");
            e.target.value = '';
            return;
        }

        const formData = new FormData();
        formData.append('document_type', type);
        formData.append('file', file);

        try {
            setUploadingType(type);
            await axios.post(`/api/v1/visitor-service-providers/${providerId}/documents`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("Document uploaded successfully");
            fetchDocuments();
        } catch (error) {
            toast.error(getErrorMessage(error, "Failed to upload document"));
        } finally {
            setUploadingType(null);
            e.target.value = ''; // Reset file input
        }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm("Are you sure you want to remove this document?")) return;
        
        try {
            await axios.delete(`/api/v1/visitor-service-providers/${providerId}/documents/${docId}`);
            toast.success("Document removed");
            fetchDocuments();
        } catch (error) {
            toast.error(getErrorMessage(error, "Failed to delete document"));
        }
    };

    const handleVerify = async (docId, status) => {
        try {
            await axios.patch(`/api/v1/visitor-service-providers/${providerId}/documents/${docId}/verify`, { status });
            toast.success(`Document ${status}`);
            fetchDocuments();
        } catch (error) {
            toast.error(getErrorMessage(error, "Failed to update status"));
        }
    };

    const renderPreview = () => {
        if (!previewDoc) return null;
        const isPdf = previewDoc.file_type?.toLowerCase() === 'pdf';

        return (
            <Modal title={`${previewDoc.document_type.replace('_', ' ').toUpperCase()} Preview`} onClose={() => setPreviewDoc(null)} size="xl">
                <div className="flex flex-col h-[70vh]">
                    {previewDoc.temporary_url ? (
                        isPdf ? (
                            <iframe src={previewDoc.temporary_url} className="w-full h-full rounded border dark:border-slate-700" title="PDF Preview" />
                        ) : (
                            <div className="flex-1 overflow-auto flex items-center justify-center bg-slate-100 dark:bg-slate-900 rounded border dark:border-slate-700">
                                <img src={previewDoc.temporary_url} alt="Document" className="max-w-full max-h-full object-contain" />
                            </div>
                        )
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-slate-500">
                            Document file is unavailable or missing.
                        </div>
                    )}
                    
                    <div className="mt-4 flex justify-between items-center pt-4 border-t dark:border-slate-700">
                        <div className="text-sm text-slate-500">
                            Uploaded by {previewDoc.uploader?.first_name || 'Admin'} on {new Date(previewDoc.created_at).toLocaleDateString()}
                        </div>
                        <div className="flex gap-2">
                            {previewDoc.status === 'uploaded' && (
                                <>
                                    <Button variant="danger" onClick={() => { handleVerify(previewDoc.id, 'rejected'); setPreviewDoc(null); }}>Reject</Button>
                                    <Button variant="success" onClick={() => { handleVerify(previewDoc.id, 'verified'); setPreviewDoc(null); }}>Verify</Button>
                                </>
                            )}
                            <Button variant="ghost" onClick={() => setPreviewDoc(null)}>Close</Button>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    if (loading) {
        return <div className="p-8 text-center text-slate-500">Loading documents...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-3 rounded-lg text-sm flex items-start gap-3">
                <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p>Identity documents contain sensitive personal information. Access is strictly logged and restricted to authorized administration personnel only.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DOCUMENT_TYPES.map(type => {
                    const doc = documents.find(d => d.document_type === type.id);
                    
                    return (
                        <div key={type.id} className="border dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800 flex flex-col justify-between shadow-sm">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="font-semibold text-slate-800 dark:text-slate-200">{type.label}</h4>
                                    {doc ? (
                                        <p className="text-xs text-slate-500 mt-1">Uploaded {new Date(doc.created_at).toLocaleDateString()}</p>
                                    ) : (
                                        <p className="text-xs text-slate-400 mt-1">Not uploaded yet</p>
                                    )}
                                </div>
                                {doc && (
                                    <Badge variant={doc.status === 'verified' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'} className="text-[10px] uppercase">
                                        {doc.status}
                                    </Badge>
                                )}
                            </div>

                            {doc ? (
                                <div className="flex items-center gap-2 mt-auto pt-4 border-t dark:border-slate-700">
                                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setPreviewDoc(doc)}>
                                        Preview
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30" onClick={() => handleDelete(doc.id)}>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </Button>
                                </div>
                            ) : (
                                <div className="mt-auto pt-4 border-t dark:border-slate-700">
                                    <label className={`block w-full text-center border-2 border-dashed rounded-lg p-3 cursor-pointer transition-colors ${uploadingType === type.id ? 'border-indigo-500 bg-indigo-50 text-indigo-600' : 'border-slate-300 dark:border-slate-600 hover:border-indigo-500 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}>
                                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => handleUpload(e, type.id)} disabled={uploadingType === type.id} />
                                        <span className="text-sm font-medium">
                                            {uploadingType === type.id ? 'Uploading...' : 'Click to Upload (Max 5MB)'}
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {renderPreview()}
        </div>
    );
}
